import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";

/**
 * Log delle email registrate/inviate (collection `mensa_email_outbox`).
 * Mostra mittente, destinatario, modello, stato (sent/error/mock), preview body.
 */
export default function EmailLogView() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ sent: 0, sent_mock: 0, error: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [filtroStatus, setFiltroStatus] = useState("");
  const [filtroTpl, setFiltroTpl] = useState("");
  const [search, setSearch] = useState("");
  const [preview, setPreview] = useState(null); // row clicked to preview
  // Cancellazione bulk per range di date (richiesta utente 2026-02-24)
  const [bulkDelFrom, setBulkDelFrom] = useState("");
  const [bulkDelTo, setBulkDelTo] = useState("");
  const [bulkDelBusy, setBulkDelBusy] = useState(false);

  const carica = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroStatus) params.status = filtroStatus;
      if (filtroTpl) params.template_id = filtroTpl;
      const r = await api.get("/mensa-matrice/email-outbox", { params });
      setItems(r.data.items || []);
      setCounts(r.data.counts || {});
    } catch (e) {
      setErr(e.response?.data?.detail || "Errore caricamento log email");
    } finally { setLoading(false); }
  };
  useEffect(() => { carica(); /* eslint-disable-next-line */ }, [filtroStatus, filtroTpl]);

  const itemsFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(i =>
      (i.to || "").toLowerCase().includes(q) ||
      (i.subject || "").toLowerCase().includes(q) ||
      (i.template_id || "").toLowerCase().includes(q) ||
      (i.alunno_label || "").toLowerCase().includes(q) ||
      (i.classe || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const elimina = async (id) => {
    if (!window.confirm("Eliminare definitivamente questa riga dal log?")) return;
    try {
      await api.delete(`/mensa-matrice/email-outbox/${id}`);
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
  };

  // Cancella TUTTE le righe del log con sent_at nel range [bulkDelFrom, bulkDelTo].
  // Conferma esplicita richiesta + spinner.
  const bulkDeleteRange = async () => {
    if (!bulkDelFrom || !bulkDelTo) { alert("Specifica entrambe le date (Dal / Al)."); return; }
    if (bulkDelTo < bulkDelFrom) { alert("La data 'Al' deve essere >= 'Dal'."); return; }
    const msg = `Stai per CANCELLARE definitivamente TUTTE le righe del log email con data invio compresa tra il ${bulkDelFrom} e il ${bulkDelTo}.\n\nL'operazione è IRREVERSIBILE.\n\nProcedere?`;
    if (!window.confirm(msg)) return;
    setBulkDelBusy(true);
    try {
      const r = await api.post(`/mensa-matrice/email-outbox/_bulk-delete-range`, { date_from: bulkDelFrom, date_to: bulkDelTo });
      alert(`Cancellate ${r.data?.deleted ?? 0} righe.`);
      await carica();
    } catch (e) { setErr(e.response?.data?.detail || "Errore"); }
    finally { setBulkDelBusy(false); }
  };

  const statusBadge = (s) => {
    const map = {
      sent: { bg: "#1b5e20", color: "white", txt: "✓ INVIATA" },
      sent_mock: { bg: "#ef6c00", color: "white", txt: "⊘ MOCK" },
      error: { bg: "#c62828", color: "white", txt: "✗ ERRORE" },
      audit: { bg: "#6a1b9a", color: "white", txt: "⚙ STATO" },
    };
    const cfg = map[s] || { bg: "#888", color: "white", txt: (s || "?").toUpperCase() };
    return <span style={{ padding: "2px 8px", background: cfg.bg, color: cfg.color, fontSize: 10, fontWeight: 700, borderRadius: 2 }}>{cfg.txt}</span>;
  };

  const statoLabel = (s) => {
    const lbl = { pending: "PENDING", confirmed: "APPROVATA", waiting: "ATTESA", rejected: "RIFIUTATA", recesso: "RECESSO" };
    return lbl[s] || (s || "?").toUpperCase();
  };

  const fmtData = (s) => {
    if (!s) return "—";
    try {
      const d = new Date(s);
      return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return s; }
  };

  return (
    <div style={{ padding: "12px 20px" }}>
      <div style={{ marginBottom: 12 }}>
        <h2 style={{ margin: 0, color: "#4a148c", fontSize: 20 }}>📬 Log Email Inviate</h2>
        <div style={{ fontSize: 11, color: "#666", marginTop: 4 }}>
          Cronologia di tutte le email registrate/inviate via il portale (ultime 200).
        </div>
      </div>

      {/* Statistiche */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        <StatCard label="Inviate" value={counts.sent || 0} color="#1b5e20" testid="emaillog-stat-sent" />
        <StatCard label="Mock" value={counts.sent_mock || 0} color="#ef6c00" testid="emaillog-stat-mock" />
        <StatCard label="Errori" value={counts.error || 0} color="#c62828" testid="emaillog-stat-err" />
        <StatCard label="Cambi stato" value={counts.audit || 0} color="#6a1b9a" testid="emaillog-stat-audit" />
      </div>

      {/* Filtri */}
      <div style={{ background: "white", border: "1px solid #ccc", padding: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <select value={filtroStatus} onChange={e => setFiltroStatus(e.target.value)} data-testid="emaillog-filter-status"
          style={{ padding: 6, border: "1px solid #ccc", fontSize: 12 }}>
          <option value="">Tutti gli stati</option>
          <option value="sent">✓ Inviate</option>
          <option value="error">✗ Errori</option>
          <option value="sent_mock">⊘ Mock</option>
          <option value="audit">⚙ Cambi stato</option>
        </select>
        <select value={filtroTpl} onChange={e => setFiltroTpl(e.target.value)} data-testid="emaillog-filter-tpl"
          style={{ padding: 6, border: "1px solid #ccc", fontSize: 12 }}>
          <option value="">Tutti i modelli</option>
          <option value="accettazione_pagamento">Accettazione + pagamento</option>
          <option value="lista_attesa">Lista d'attesa</option>
          <option value="conferma_pagamento">Conferma pagamento</option>
        </select>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca per destinatario/oggetto..."
          data-testid="emaillog-search"
          style={{ flex: 1, minWidth: 200, padding: 6, border: "1px solid #ccc", fontSize: 12 }} />
        <button onClick={carica} data-testid="emaillog-refresh"
          style={{ padding: "6px 14px", background: "#1565c0", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>↻ Aggiorna</button>
      </div>

      {/* Cancellazione bulk per range di date */}
      <div style={{ background: "#fff8e1", border: "1px solid #ffc107", padding: 10, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#bf360c" }}>🗑️ Cancella range invii:</span>
        <label style={{ fontSize: 12, color: "#444" }}>Dal:
          <input type="date" value={bulkDelFrom} onChange={e => setBulkDelFrom(e.target.value)}
            data-testid="emaillog-bulkdel-from"
            style={{ marginLeft: 4, padding: 4, border: "1px solid #ccc", fontSize: 12 }} />
        </label>
        <label style={{ fontSize: 12, color: "#444" }}>Al:
          <input type="date" value={bulkDelTo} onChange={e => setBulkDelTo(e.target.value)}
            data-testid="emaillog-bulkdel-to"
            style={{ marginLeft: 4, padding: 4, border: "1px solid #ccc", fontSize: 12 }} />
        </label>
        <button onClick={bulkDeleteRange} disabled={!bulkDelFrom || !bulkDelTo || bulkDelBusy}
          data-testid="emaillog-bulkdel-btn"
          title="Cancella TUTTE le righe del log con data invio compresa nel range. Operazione irreversibile, riservata all'admin."
          style={{ padding: "6px 14px", background: bulkDelBusy ? "#999" : "#c62828", color: "white", border: "none", fontSize: 12, fontWeight: 700, cursor: bulkDelBusy ? "wait" : "pointer", borderRadius: 3 }}>
          {bulkDelBusy ? "⏳ Cancellazione..." : "🗑️ Cancella range"}
        </button>
        <span style={{ fontSize: 10, color: "#7b1fa2", marginLeft: "auto", fontStyle: "italic" }}>
          ⚠ Operazione IRREVERSIBILE — cancella sia invii andati a buon fine sia eventuali errori/mock/cambi stato.
        </span>
      </div>

      {err && <div style={{ marginBottom: 12, padding: 10, background: "#fff0f0", color: "#c00", fontSize: 12, border: "1px solid #fcc" }}>{err}</div>}

      <div style={{ background: "white", border: "1px solid #ccc" }}>
        <table className="sw-table sw-table-compact" style={{ width: "100%", fontSize: 11 }}>
          <thead style={{ background: "#1f3864", color: "white" }}>
            <tr>
              <th style={{ textAlign: "center", width: 110 }}>DATA</th>
              <th style={{ textAlign: "center", width: 90 }}>STATO</th>
              <th style={{ width: 130 }}>MODELLO</th>
              <th style={{ width: 160 }}>ALUNNO</th>
              <th style={{ textAlign: "center", width: 55 }}>SETT.</th>
              <th style={{ textAlign: "center", width: 70 }}>CLASSE</th>
              <th>DESTINATARIO</th>
              <th>OGGETTO</th>
              <th style={{ width: 160 }}>INVIATA DA</th>
              <th style={{ textAlign: "center", width: 70 }}>AZIONI</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={10} style={{ padding: 30, textAlign: "center", color: "#888" }}>Caricamento...</td></tr>}
            {!loading && itemsFiltered.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 20, textAlign: "center", color: "#888" }}>
                Nessuna email registrata. Quando invierai un'email dalla lista iscrizioni, comparirà qui.
              </td></tr>
            )}
            {!loading && itemsFiltered.map(it => (
              <tr key={it._id} style={{ borderTop: "1px solid #eee", cursor: "pointer", background: it.status === "audit" ? "#faf5fb" : undefined }}
                onClick={() => setPreview(it)} data-testid={`emaillog-row-${it._id}`}>
                <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontSize: 10, color: "#555" }} title={it.sent_at}>{fmtData(it.sent_at)}</td>
                <td style={{ textAlign: "center" }}>{statusBadge(it.status)}</td>
                <td style={{ fontWeight: 700, color: it.status === "audit" ? "#6a1b9a" : "#1565c0" }}>
                  {it.status === "audit" && it.from_stato && it.to_stato
                    ? <span style={{ fontFamily: "Consolas, monospace", fontSize: 10 }}>{statoLabel(it.from_stato)} → <b>{statoLabel(it.to_stato)}</b></span>
                    : it.template_id}
                </td>
                <td style={{ fontWeight: 600, color: "#37474f" }} title={it.corso ? `Corso: ${it.corso}` : ""}>
                  {it.alunno_label || <span style={{ color: "#ccc" }}>—</span>}
                  {(it.data_recesso || it.iscrizione_deleted) && (
                    <span
                      data-testid={`emaillog-recesso-${it._id}`}
                      title={
                        it.data_recesso
                          ? `Iscrizione DISDETTA il ${fmtData(it.data_recesso)}`
                          : "Iscrizione RIMOSSA (recesso storico)"
                      }
                      style={{
                        marginLeft: 6, padding: "1px 6px", background: "#c62828", color: "white",
                        fontSize: 9, fontWeight: 800, borderRadius: 2, letterSpacing: 0.3,
                        verticalAlign: "middle",
                      }}
                    >
                      {it.data_recesso
                        ? `⊘ DISDETTA ${fmtData(it.data_recesso).slice(0, 8)}`
                        : "⊘ RECESSO"}
                    </span>
                  )}
                </td>
                <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700, color: "#2e7d32" }}>{it.settore || <span style={{ color: "#ccc" }}>—</span>}</td>
                <td style={{ textAlign: "center", fontFamily: "Consolas, monospace", fontWeight: 700, color: "#0d47a1" }}>{it.classe || <span style={{ color: "#ccc" }}>—</span>}</td>
                <td style={{ fontFamily: "Consolas, monospace", fontSize: 11 }}>{it.to || (it.status === "audit" ? <span style={{ color: "#999", fontStyle: "italic" }}>—</span> : "")}</td>
                <td style={{ color: "#333" }} title={it.subject}>{(it.subject || "").length > 60 ? (it.subject.slice(0, 60) + "…") : it.subject}</td>
                <td style={{ fontSize: 10, color: "#666", fontFamily: "Consolas, monospace" }} title={it.sent_by_email || it.sent_by}>{it.sent_by_email || it.sent_by || "—"}</td>
                <td style={{ textAlign: "center" }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => elimina(it._id)} data-testid={`emaillog-del-${it._id}`}
                    title="Elimina log"
                    style={{ background: "#c62828", color: "white", border: "none", padding: "3px 7px", fontSize: 10, fontWeight: 700, cursor: "pointer" }}>🗑</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && (
        <div onClick={() => setPreview(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div onClick={e => e.stopPropagation()} data-testid="emaillog-preview"
            style={{ background: "white", width: "100%", maxWidth: 720, borderRadius: 6, overflow: "hidden", maxHeight: "92vh", display: "flex", flexDirection: "column" }}>
            <div style={{ background: "#1f3864", color: "white", padding: "12px 18px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <strong>📬 Dettaglio email</strong>
              <button onClick={() => setPreview(null)} data-testid="emaillog-preview-close" style={{ background: "none", border: "none", color: "white", cursor: "pointer", fontSize: 18 }}>✕</button>
            </div>
            <div style={{ padding: 18, overflowY: "auto", fontSize: 12 }}>
              <table style={{ width: "100%", marginBottom: 12 }}>
                <tbody>
                  <Row label="Stato">{statusBadge(preview.status)}</Row>
                  {preview.status === "audit" && preview.from_stato && preview.to_stato && (
                    <Row label="Transizione"><span style={{ fontFamily: "Consolas, monospace", fontWeight: 700 }}>{statoLabel(preview.from_stato)} → <span style={{ color: "#6a1b9a" }}>{statoLabel(preview.to_stato)}</span></span></Row>
                  )}
                  <Row label="Provider">{preview.provider || "—"}{preview.provider_message_id ? <span style={{ marginLeft: 8, fontFamily: "Consolas, monospace", color: "#888", fontSize: 10 }}>id: {preview.provider_message_id}</span> : null}</Row>
                  <Row label="Data">{fmtData(preview.sent_at)}</Row>
                  <Row label="Mittente">{preview.from_name ? `${preview.from_name} <${preview.from_email}>` : preview.from_email}</Row>
                  <Row label="Destinatario"><b>{preview.to}</b></Row>
                  <Row label="Alunno">{preview.alunno_label || <span style={{ color: "#999" }}>—</span>}{preview.corso && <span style={{ color: "#666", marginLeft: 6, fontSize: 11 }}>· {preview.corso}</span>}</Row>
                  {(preview.data_recesso || preview.iscrizione_deleted) && (
                    <Row label="Iscrizione">
                      <span data-testid="emaillog-preview-recesso" style={{ padding: "2px 8px", background: "#c62828", color: "white", fontSize: 11, fontWeight: 700, borderRadius: 2 }}>
                        {preview.data_recesso
                          ? `⊘ DISDETTA il ${fmtData(preview.data_recesso)}`
                          : "⊘ RECESSO (iscrizione rimossa)"}
                      </span>
                    </Row>
                  )}
                  <Row label="Settore / Classe">{[preview.settore, preview.classe].filter(Boolean).join(" · ") || <span style={{ color: "#999" }}>—</span>}</Row>
                  <Row label="Modello">{preview.template_id}</Row>
                  <Row label="Inviata da"><span style={{ fontFamily: "Consolas, monospace" }}>{preview.sent_by_email || preview.sent_by || "—"}</span></Row>
                  {preview.error && <Row label="Errore"><span style={{ color: "#c62828", fontWeight: 700 }}>{preview.error}</span></Row>}
                </tbody>
              </table>
              <div style={{ marginBottom: 6, fontSize: 11, color: "#666", fontWeight: 700 }}>OGGETTO</div>
              <div style={{ padding: 10, background: "#f5f5f5", fontWeight: 600, marginBottom: 12, fontSize: 13 }}>{preview.subject}</div>
              <div style={{ marginBottom: 6, fontSize: 11, color: "#666", fontWeight: 700 }}>CORPO</div>
              <div style={{ padding: 12, background: "#fafafa", border: "1px solid #eee", whiteSpace: "pre-wrap", fontFamily: "Consolas, monospace", fontSize: 11, lineHeight: 1.5 }}>{preview.body}</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color, testid }) {
  return (
    <div data-testid={testid} style={{ background: "white", border: `2px solid ${color}`, padding: "8px 14px", minWidth: 110, textAlign: "center", borderRadius: 4 }}>
      <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: 10, color: "#666", marginTop: 4, textTransform: "uppercase", fontWeight: 700 }}>{label}</div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <tr style={{ borderBottom: "1px solid #eee" }}>
      <td style={{ padding: "6px 8px", color: "#888", fontWeight: 700, fontSize: 10, textTransform: "uppercase", width: 110, verticalAlign: "top" }}>{label}</td>
      <td style={{ padding: "6px 8px" }}>{children}</td>
    </tr>
  );
}
